/**
 * Who you are, on every page. Account (an `HttpOnly` cookie) and player (per-code
 * `localStorage`) are separate identities and drawn differently; see `lib/whoami.ts`.
 * Not rendered on `/board/:code`: a TV has no user. The workshop pages have no bar: the
 * workshop hangs the account as one of its own plaques (`variant="workshop"`).
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  accountNow,
  getVersion,
  loadAccount,
  playerIn,
  signOut,
  subscribe,
} from '../lib/whoami';

/** Routes that cannot be stood on without an account (`AuthGate` fronts each). */
const GATED = ['/host/', '/create', '/admin', '/site-admin'];

/** The routes the workshop draws (`App.tsx`). They carry the account plaque, not the bar. */
const WORKSHOP = new Set(['/', '/create', '/play-now', '/signin']);

/** `/play/H7YC` → `H7YC`. Null anywhere a session code is not in the path. */
function codeFromPath(pathname: string): string | null {
  const match = /^\/(?:play|host|board)\/([^/]+)/u.exec(pathname);
  return match?.[1] ? decodeURIComponent(match[1]).toUpperCase() : null;
}

/** Falls back to the whole string so a blank-ish name never leaves an empty trigger. */
function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/u)[0] || displayName;
}

export function IdentityBar({ variant = 'bar' }: { variant?: 'bar' | 'workshop' }) {
  const ws = variant === 'workshop';
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // A version counter: React can't watch the cookie or `localStorage`, so the store says
  // when to re-read and the reads below do the looking.
  useSyncExternalStore(subscribe, getVersion, getVersion);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void loadAccount();
  }, []);

  // Close on navigation too, or a link inside leaves it open over the new page.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // `pointerdown`, not an overlay: a click elsewhere should close this and still press
    // what it landed on.
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const code = codeFromPath(pathname);
  const account = accountNow();
  const player = playerIn(code);

  const onSignOut = useCallback(() => {
    setBusy(true);
    void signOut().finally(() => {
      setBusy(false);
      setOpen(false);
      if (GATED.some((prefix) => pathname.startsWith(prefix))) navigate('/');
    });
  }, [navigate, pathname]);

  if (pathname.startsWith('/board/')) return null;
  if (!ws && WORKSHOP.has(pathname)) return null;

  const home = (
    <Link className="id-home" to="/">
      Game Show Forge
    </Link>
  );

  // Frame only until `/auth/me` answers: no "Not signed in" flash, no layout shift.
  if (account === undefined) {
    if (ws) return null;
    return (
      <div className="id-bar">
        {home}
        <span />
      </div>
    );
  }

  if (account) {
    const menu = (
      <div className={ws ? 'ws-account' : 'id-menu'} ref={menuRef}>
        <button
          className={ws ? 'ws-account-btn' : 'id-user'}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={ws ? `Account: ${account.displayName}` : undefined}
          onClick={() => setOpen((was) => !was)}
        >
          {ws ? (
            <span className="ws-account-mark" aria-hidden="true">
              {firstName(account.displayName).charAt(0).toUpperCase()}
            </span>
          ) : (
            <span className="lb-dot is-on" aria-hidden="true" />
          )}
          <strong>{firstName(account.displayName)}</strong>
          <span className="id-caret" aria-hidden="true">
            ▾
          </span>
        </button>
        {open ? (
          <div className="id-pop" aria-label="Account">
            <div className="id-pop-who">
              <strong>{account.displayName}</strong>
              {account.email ? <span className="faint">{account.email}</span> : null}
            </div>
            <div className="id-pop-sep" />
            <Link className="id-pop-item" to="/admin">
              Account
            </Link>
            <Link className="id-pop-item" to="/friends">
              Friends
            </Link>
            <Link className="id-pop-item" to="/leaderboard">
              Leaderboard
            </Link>
            {account.isAdmin ? (
              <Link className="id-pop-item" to="/site-admin">
                Site admin
              </Link>
            ) : null}
            <button className="id-pop-item" disabled={busy} onClick={onSignOut}>
              {busy ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        ) : null}
      </div>
    );
    return ws ? (
      menu
    ) : (
      <div className="id-bar">
        {home}
        {menu}
      </div>
    );
  }

  if (ws) {
    return (
      <div className="ws-account">
        <Link className="ws-account-btn" to="/signin" aria-label="Sign in">
          {/* A phone shows only this mark: a head and shoulders. */}
          <span className="ws-account-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" />
            </svg>
          </span>
          <strong>Sign in</strong>
        </Link>
      </div>
    );
  }

  return (
    <div className="id-bar">
      {home}
      <div className="id-menu">
        <span className="id-who">
          <span className="lb-dot" aria-hidden="true" />
          {player ? (
            <>
              <strong>{player.name}</strong>
              <span className="faint"> · on this device</span>
            </>
          ) : (
            <span className="faint">Not signed in</span>
          )}
        </span>
        {/* Back here afterwards: a phone signing in mid-game wants its game, not the workshop. */}
        <Link className="btn btn-ghost btn-sm" to={`/signin?next=${encodeURIComponent(pathname)}`}>
          Sign in
        </Link>
      </div>
    </div>
  );
}
